import React from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { formatDate, DISPLAY_FORMAT, formatHotelPortalCancellationDisplayDate } from "../../helpers/dateHelper";

dayjs.extend(customParseFormat);

export const dateFormat = "DD-MM-YYYY";
export const oriDateFormat = "YYYY-MM-DD";

/** Match flight ticket labels (AllTicketStatus getFlightTicketStatus). */
export const getFlightReportStatusLabel = (record) => {
  const code = Number(record?.bookingStatus);
  switch (code) {
    case 1:
      return "CREATED";
    case 2:
      return "BLOCKED";
    case 3:
      return "CONFIRMED";
    case 4:
      return "CANCELLED";
    case 5:
      return "PARTIALLY CANCELLED";
    case 6:
      return "CANCELLATION REQUESTED";
    case 7:
      return "RESCHEDULE REQUESTED";
    case 8:
      return "RESCHEDULE CONFIRMED";
    case 10:
      return "HOLD";
    default: {
      const raw = record?.BookingStatus;
      if (!raw) return "";
      return String(raw).replace(/_/g, " ");
    }
  }
};

/** Match hotel ticket labels (hotelhelper getStatus). */
export const getHotelReportStatusLabel = (record) => {
  const code = Number(record?.bookingStatus);
  switch (code) {
    case 10:
      return "CONFIRMED";
    case 3:
    case 11:
      return "VOUCHERED";
    case 6:
      return "CANCELLED";
    case 7:
      return "PENDING";
    case 2:
      return "BLOCKED";
    case 4:
      return "FAILED";
    case 5:
      return "REJECTED";
    case 8:
      return "CANCELLATIONREQUESTED";
    case 9:
      return "CANCELLATIONPENDING";
    default:
      return record?.BookingStatus === "CANCELED"
        ? "CANCELLED"
        : record?.BookingStatus === "HOLD"
          ? "CONFIRMED"
          : record?.BookingStatus === "CONFIRMED" && code === 3
            ? "VOUCHERED"
            : record?.BookingStatus;
  }
};

/** Confirmed/Hold → Last Voucher Date; Vouchered → Last Cancellation Date (portal −1 day offset). */
export const getHotelConfirmedCancellationDisplayDate = (record) => {
  const code = Number(record?.bookingStatus);
  const isVouchered =
    code === 3 || code === 11 || record?.BookingStatus === "VOUCHERED";
  const isConfirmedOrHold =
    code === 10 || record?.BookingStatus === "HOLD";

  if (isVouchered && record?.LastCancellationDate) {
    return formatHotelPortalCancellationDisplayDate(
      record.LastCancellationDate,
      "DD-MM-YYYY",
    );
  }
  if (isConfirmedOrHold && record?.LastVoucherDate) {
    return formatHotelPortalCancellationDisplayDate(
      record.LastVoucherDate,
      "DD-MM-YYYY",
    );
  }
  return null;
};

export const renderReffrence = (ref, rec) => {
  const type = rec.type;
  let url = "";
  switch (type) {
    case "Flight": {
      url = "/admin/flight/ticket";
      break;
    }
    case "Bus": {
      url = "/admin/bus/ticket";
      break;
    }
    case "Hotel": {
      url = "/admin/hotel/ticket";
      break;
    }
    case "Activities": {
      url = "/admin/activities/ticket";
      break;
    }
    case "BuildPackage": {
      url = "/admin/buildpackage/ticket";
      break;
    }
  }

  return <Link target="_blank" to={url + "?ref=" + ref}>{ref}</Link>;
};

export const getFlightStatus = (status, record) => {
  const label = getFlightReportStatusLabel(record) || status;
  return getStatus(label, record);
};

export const getHotelStatus = (status, record) => {
  // Prefer numeric bookingStatus → ticket labels (fixes VOUCHERED / HOLD→CONFIRMED)
  if (record && (record.bookingStatus != null || record.type === "Hotel")) {
    const label = getHotelReportStatusLabel(record);
    if (label) {
      return getStatus(label, record);
    }
  }
  switch (status) {
    case "FAILED": {
      return <p style={{ color: "#FFA500" }}>FAILED</p>;
    }
    case "CONFIRMED": {
      return <p style={{ color: "#008000" }}>CONFIRMED</p>;
    }
    case "CANCELED":
    case "CANCELLED": {
      return <p style={{ color: "#FF0000" }}>CANCELLED</p>;
    }
    case "PENDING": {
      return <p style={{ color: "#FF0000" }}>PENDING</p>;
    }
    case "REJECTED": {
      return <p style={{ color: "#f9e218" }}>REJECTED</p>;
    }
    case "HOLD": {
      // Ticket shows HOLD as CONFIRMED
      return <p style={{ color: "#008000" }}>CONFIRMED</p>;
    }
    case "VOUCHERED": {
      return <p style={{ color: "#FF0000" }}>VOUCHERED</p>;
    }
    case "BLOCKED": {
      return <p style={{ color: "#FFA500" }}>BLOCKED</p>;
    }
    case "CANCELLATION REQUESTED":
    case "CANCELLATIONREQUESTED": {
      return <p style={{ color: "#FF0000" }}>CANCELLATION REQUESTED</p>;
    }
    case "CANCELLATIONPENDING": {
      return <p style={{ color: "#FF0000" }}>CANCELLATIONPENDING</p>;
    }
    case "CANCELLATIONPROGRESS": {
      return <p style={{ color: "#FF0000" }}>CANCELLATIONPROGRESS</p>;
    }
    default:
      return getStatus(status, record);
  }
};

export const getStatus = (status, record) => {
  const label =
    record?.type === "Flight"
      ? getFlightReportStatusLabel(record) || status
      : status;
  switch (label) {
    case "BLOCKED": {
      return <p style={{ color: "#FFA500" }}>{label}</p>;
    }
    case "CONFIRMED": {
      return <p style={{ color: "#008000" }}>{label}</p>;
    }
    case "BOOKED": {
      return <p style={{ color: "#008000" }}>{label}</p>;
    }
    case "CANCELLED": {
      return <p style={{ color: "#FF0000" }}>{label}</p>;
    }
    case "ON REQUEST": {
      return <p style={{ color: "#ff0000" }}>{label}</p>;
    }
    case "CREATED": {
      return <p style={{ color: "#f9e218" }}>{label}</p>;
    }
    case "HOLD": {
      return <p style={{ color: "#FF0000" }}>{label}</p>;
    }
    case "PARTIALLY CANCELLED":
    case "PARTIALLY_CANCELLED": {
      return <p style={{ color: "#FF0000" }}>PARTIALLY CANCELLED</p>;
    }
    case "CANCELLATION REQUESTED":
    case "CANCELLATIONREQUESTED": {
      return <p style={{ color: "#FF0000" }}>CANCELLATION REQUESTED</p>;
    }
    case "RESCHEDULE REQUESTED":
    case "RESCHEDULEREQUESTED": {
      return <p style={{ color: "#FF0000" }}>RESCHEDULE REQUESTED</p>;
    }
    case "RESCHEDULE CONFIRMED":
    case "RESCHEDULECONFIRMED": {
      return <p style={{ color: "#008000" }}>RESCHEDULE CONFIRMED</p>;
    }
    case "ALREADYCANCELLED": {
      return <p style={{ color: "#FF0000" }}>{label}</p>;
    }
    case "PENDING": {
      return <p style={{ color: "#FF0000" }}>{label}</p>;
    }
    case "FAILED": {
      return <p style={{ color: "#FF0000" }}>{label}</p>;
    }
    case "REJECTED": {
      return <p style={{ color: "#FF0000" }}>{label}</p>;
    }
    case "CancellationRequest": {
      return <p style={{ color: "#FF0000" }}>{label}</p>;
    }
    case "CancellationPending": {
      return <p style={{ color: "#FF0000" }}>{label}</p>;
    }
    case "CancellationInProgress": {
      return <p style={{ color: "#FF0000" }}>{label}</p>;
    }
    case "VOUCHERED": {
      return <p style={{ color: "#FF0000" }}>{label}</p>;
    }
    default:
      return label ? <p>{label}</p> : null;
  }
};

export const allColumns = [
  {
    title: "Bked By",
    dataIndex: "BookedBy",
  },
  {
    title: "Provider",
    dataIndex: "provider",
    key: "provider",
  },
  {
    title: "opr",
    dataIndex: "operator",
    key: "operator",
  },
  {
    title: "RefNo",
    dataIndex: "referenceNumber",
    render: (text, record) => renderReffrence(text, record),
    sorter: (a, b) => a.referenceNumber?.localeCompare(b.referenceNumber),
  },

  {
    title: "Serv Type",
    dataIndex: "type",
    key: "type",
  },

  {
    title: "Src",
    dataIndex: "from",
    render: (text, record) => {
      return record.type == "Flight" ? record?.oneWaySegment[0]?.origin : text;
    },
  },
  {
    title: "Dest",
    dataIndex: "to",
    render: (_, record) => {
      if (record.type == "Flight") {
        let length = record.oneWaySegment.length;
        return record?.oneWaySegment[length - 1]?.destination;
      } else {
        return "";
      }
    },
  },
  {
    title: "Htl Name",
    dataIndex: "HotelName",
    key: "HotelName",
    render: (text, rec) => (rec.type == "Hotel" ? rec.HotelName : text),
  },
  {
    title: "Jrny Date",
    dataIndex: "journeyDate",
    key: "journeyDate",
    render: (text, record) => {
      if (record.type == "Flight") {
        return text ? formatDate(text, "DD-MM-YYYY") : null;
      } else {
        return text;
      }
    },
  },
  {
    title: "Arr Trml",
    dataIndex: "oneWaySegment",
    render: (text, record) =>
      record.type == "Flight" ? text[0]?.arrivalTerminal : "",
  },
  {
    title: "Arr Date",
    dataIndex: "oneWaySegment",
    render: (text, record) =>
      record.type == "Flight"
        ? formatDate(text[0]?.arrivalDateTime, dateFormat)
        : "",
  },

  {
    title: "Dept Trml",
    dataIndex: "oneWaySegment",
    render: (text, record) => {
      if (record.type == "Flight") {
        let length = text.length;
        return text[length - 1]?.departureTerminal;
      } else {
        return "";
      }
    },
  },
  {
    title: "Dept Date",
    dataIndex: "oneWaySegment",
    render: (text, record) => {
      if (record.type == "Flight") {
        let length = text.length;
        return formatDate(
          text[length - 1]?.departureDateTime,
          dateFormat
        );
      } else {
        return "";
      }
    },
  },
  {
    title: "ChkIn Date",
    dataIndex: "CheckInDate",
    key: "CheckInDate",
    render: (text) =>
      text ? formatDate(text, dateFormat) : "",
  },
  {
    title: "ChkOut Date",
    dataIndex: "CheckOutDate",
    key: "CheckOutDate",
    render: (text) =>
      text ? formatDate(text, dateFormat) : "",
  },

  {
    title: "Adults",
    dataIndex: "pax",
    render: (text, record) =>
      // record.type == "Flight" ? record.pax.adults : record.AdultCount,
     record.pax ? record.pax.adults : record.AdultCount 
  },
  {
    title: "Childs",
    dataIndex: "pax",
    render: (text, record) =>
      // record.type == "Flight" ? record.pax.childCount : record.ChildCount,
    record.pax ? record.pax.childCount : record.ChildCount
  },
  {
    title: "Infant",
    dataIndex: "pax",
    render: (text, record) =>
      // (record.type == "Flight" ? text.infantCount : 0),
    record.pax ? record.pax.infantCount : 0
  },
  {
    title: "Optr Name",
    dataIndex: "operator",
    key: "operator",
  },

  {
    title: "Bkng Status",
    dataIndex: "BookingStatus",
    key: "status",

    render: (status, record) => {
      return record.type === "Hotel"
        ? getHotelStatus(status, record)
        : record.type === "Flight"
          ? getFlightStatus(status, record)
          : getStatus(status, record);
    },
  },
  {
    title: "Last Vouchered/Cancellation Date",
    key: "hotelConfirmedCancellationDate",
    render: (_, record) =>
      record.type === "Hotel"
        ? getHotelConfirmedCancellationDisplayDate(record)
        : null,
  },
  {
    title: "Pax Name ",
    dataIndex: "guestName",
  },
  {
    title: "Pax Email",
    dataIndex: "guestEmaiId",
  },
  {
    title: "Pax Mobile ",
    dataIndex: "guestMobileNo",
  },
  {
    title: "Pymnt Mode",
    dataIndex: "isPartPayment",
    render: (isPartPayment, rec) =>
      isPartPayment && rec?.BookingStatus === "CONFIRMED"
        ? isPartPayment === true
          ? "Part Payment"
          : "Full Payment"
        : "",
  },
  {
    title: "Due Amt(Rs.)",
    dataIndex: "isPartPayment",
    render: (isPartPayment, rec) =>
      isPartPayment && rec?.BookingStatus === "CONFIRMED" ? (
        isPartPayment === true && rec?.dueAmount ? (
          <p style={{ color: "#FF0000", fontWeight: "600" }}>
            {Number(rec?.dueAmount).toFixed(2)}
          </p>
        ) : (
          ""
        )
      ) : (
        ""
      ),
  },
  {
    title: "Part Pymnt Amt(Rs.)",
    dataIndex: "isPartPayment",
    render: (isPartPayment, rec) =>
      isPartPayment && rec?.BookingStatus === "CONFIRMED"
        ? isPartPayment === true && rec?.partPaymentAmount
          ? rec?.partPaymentAmount
          : ""
        : "",
  },
  {
    title: "Amt(Rs.)",
    dataIndex: "totalPrice",
    render: (price, rec) =>
      rec?.type === "Activities" ? rec.serviceTotal : price,
  },
];

export const activitiesColumns = [
  {
    title: "Reference number",
    dataIndex: "referenceNumber",
    render: (text, record) => renderReffrence(text, record),
    sorter: (a, b) => a.referenceNumber?.localeCompare(b.referenceNumber),
  },
  {
    title: "Tour Name",
    dataIndex: "tourName",
    sorter: (a, b) => a.tourName?.localeCompare(b.tourName),
  },
  {
    title: "Tour Option",
    dataIndex: "optionName",

    sorter: (a, b) => a.optionName?.localeCompare(b.optionName),
  },
  {
    title: "Booking Date",
    // dataIndex: "journeyDate",
    key: "bookingDate",
    render: (text) =>
      text.bookingDate != undefined
        ? formatDate(text.bookingDate, "DD-MM-YYYY")
        : "",
    sorter: (a, b) => a.bookingDate?.localeCompare(b.bookingDate),
  },
  {
    title: "Jrny Date",
    dataIndex: "journeyDate",
  },

  {
    title: "Pax Name ",
    dataIndex: "guestName",
    sorter: (a, b) => a.guestName?.localeCompare(b.guestName),
  },
  {
    title: "PAx Email",
    dataIndex: "guestEmaiId",
    sorter: (a, b) => a.guestEmaiId?.localeCompare(b.guestEmaiId),
  },
  {
    title: "Pax Mobile ",
    dataIndex: "guestMobileNo",
    sorter: (a, b) => a.guestMobileNo?.localeCompare(b.guestMobileNo),
  },
  {
    title: "Adults",
    dataIndex: "pax",
    render: (text) => text.adults,
  },
  {
    title: "Childs",
    dataIndex: "pax",
    render: (text) => text.childCount,
  },
  {
    title: "Infant",
    dataIndex: "pax",
    render: (text) => text.infantCount,
  },
  {
    title: "Booking Status",
    dataIndex: "BookingStatus",
    key: "BookingStatus",
    render: (BookingStatus, record) => getStatus(BookingStatus, record),
    sorter: (a, b) => a.BookingStatus?.localeCompare(b.BookingStatus),
  },
  {
    title: "Pymnt Mode",
    dataIndex: "isPartPayment",
    render: (isPartPayment, rec) =>
      isPartPayment && rec?.BookingStatus === "CONFIRMED"
        ? isPartPayment === true
          ? "Part Payment"
          : "Full Payment"
        : "",
  },
  {
    title: "Due Amt(Rs.)",
    dataIndex: "isPartPayment",
    render: (isPartPayment, rec) =>
      isPartPayment && rec?.BookingStatus === "CONFIRMED" ? (
        isPartPayment === true && rec?.dueAmount ? (
          <p style={{ color: "#FF0000", fontWeight: "600" }}>
            {Number(rec?.dueAmount).toFixed(2)}
          </p>
        ) : (
          ""
        )
      ) : (
        ""
      ),
  },
  {
    title: "Part Pymnt Amt(Rs.)",
    dataIndex: "isPartPayment",
    render: (isPartPayment, rec) =>
      isPartPayment && rec?.BookingStatus === "CONFIRMED"
        ? isPartPayment === true && rec?.partPaymentAmount
          ? rec?.partPaymentAmount
          : ""
        : "",
  },
  {
    title: "Amt(Rs.)",
    dataIndex: "serviceTotal",
  },
];

export const buildPackageColumns = [
  {
    title: "Reference number",
    dataIndex: "referenceNumber",
    render: (text, record) => renderReffrence(text, record),
    sorter: (a, b) => a.referenceNumber?.localeCompare(b.referenceNumber),
  },
  {
    title: "Booking Date",
    // dataIndex: "journeyDate",
    key: "bookingDate",
    render: (text) =>
      text.bookingDate != undefined
        ? formatDate(text.bookingDate, "DD-MM-YYYY")
        : "",
    sorter: (a, b) => a.bookingDate?.localeCompare(b.bookingDate),
  },
  {
    title: "Jrny Date",
    dataIndex: "journeyDate",
  },

  {
    title: "Pax Name ",
    dataIndex: "guestName",
    sorter: (a, b) => a.guestName?.localeCompare(b.guestName),
  },
  {
    title: "PAx Email",
    dataIndex: "guestEmaiId",
    sorter: (a, b) => a.guestEmaiId?.localeCompare(b.guestEmaiId),
  },
  {
    title: "Pax Mobile ",
    dataIndex: "guestMobileNo",
    sorter: (a, b) => a.guestMobileNo?.localeCompare(b.guestMobileNo),
  },
  {
    title: "Adults",
    dataIndex: "pax",
    render: (text) => text.adults,
  },
  {
    title: "Childs",
    dataIndex: "pax",
    render: (text) => text.childCount,
  },
  {
    title: "Infant",
    dataIndex: "pax",
    render: (text) => text.infantCount,
  },
  {
    title: "Booking Status",
    dataIndex: "BookingStatus",
    key: "BookingStatus",
    render: (BookingStatus, record) => getStatus(BookingStatus, record),
    sorter: (a, b) => a.BookingStatus?.localeCompare(b.BookingStatus),
  },
  {
    title: "Payment Mode",
    dataIndex: "PartPayment",
    render: (PartPayment) =>
      PartPayment?.isPartPayment ? "Part Payment" : "Full Payment",
  },
  {
    title: "Due Amount",
    dataIndex: "PartPayment",
    render: (PartPayment) =>
      PartPayment?.dueAmount ? (
        <p style={{ color: "#FF0000", fontWeight: "600" }}>
          {Number(PartPayment?.dueAmount).toFixed(2)}
        </p>
      ) : (
        ""
      ),
  },
  {
    title: "Part Payment Amount",
    dataIndex: "PartPayment",
    render: (PartPayment) => PartPayment?.partPaymentAmount ?? "",
  },
  {
    title: "Total Amount",
    dataIndex: "serviceTotal",
  },
];
export const busColumns = [
  {
    title: "Booked By",
    dataIndex: "BookedBy",
  },
  {
    title: "RefNo",
    dataIndex: "referenceNumber",
    render: (text, record) => renderReffrence(text, record),
    sorter: (a, b) => a.referenceNumber?.localeCompare(b.referenceNumber),
  },
  {
    title: "opr",
    dataIndex: "operator",
    key: "operator",
  },
  {
    title: "Bkng Date",
    // dataIndex: "journeyDate",
    key: "bookingDate",
    render: (text) =>
      text.bookingDate != undefined
        ? formatDate(text.bookingDate, "DD-MM-YYYY")
        : "",
    sorter: (a, b) => a.bookingDate?.localeCompare(b.bookingDate),
  },
  {
    title: "Jrny Date",
    dataIndex: "journeyDate",
  },
  {
    title: "Src",
    dataIndex: "from",
    key: "from",
  },
  {
    title: "Dest",
    dataIndex: "to",
    key: "from",
  },
  {
    title: "Pax Name ",
    dataIndex: "guestName",
    sorter: (a, b) => a.guestName - b.guestName,
  },
  {
    title: "Amt(Rs.)",
    dataIndex: "totalPrice",
  },
  {
    title: "Pax Email",
    dataIndex: "guestEmaiId",
    sorter: (a, b) => a.guestEmaiId - b.guestEmaiId,
  },
  {
    title: "Pax Mobile ",
    dataIndex: "guestMobileNo",
    sorter: (a, b) => a.guestMobileNo - b.guestMobileNo,
  },
  {
    title: "Bkng Status",
    dataIndex: "BookingStatus",
    render: (BookingStatus, record) => getStatus(BookingStatus, record),
    sorter: (a, b) => a.BookingStatus - b.BookingStatus,
  },
];

export const hotelColumns = [
  {
    title: "Bked By",
    dataIndex: "BookedBy",
  },
  {
    title: "RefNo",
    dataIndex: "referenceNumber",
    render: (text, record) => renderReffrence(text, record),
    sorter: (a, b) => a.referenceNumber?.localeCompare(b.referenceNumber),
  },
  {
    title: "Htl Name",
    dataIndex: "HotelName",
    key: "HotelName",
  },
  {
    title: "ChkIn Date",
    dataIndex: "CheckInDate",
    key: "CheckInDate",
    render: (text) => formatDate(text, dateFormat),
  },
  {
    title: "ChkOut Date",
    dataIndex: "CheckOutDate",
    key: "CheckOutDate",
    render: (text) => formatDate(text, dateFormat),
  },
  {
    title: "Last Vouchered/Cancellation Date",
    key: "hotelConfirmedCancellationDate",
    render: (_, record) => getHotelConfirmedCancellationDisplayDate(record),
  },
  {
    title: "Bkng Status",
    key: "BookingStatus",
    render: (_, record) => getHotelStatus(record.BookingStatus, record),
    sorter: (a, b) =>
      getHotelReportStatusLabel(a)?.localeCompare(getHotelReportStatusLabel(b)),
  },
  {
    title: "Rooms",
    dataIndex: "NoOfRooms",
  },
  {
    title: "Adults",
    dataIndex: "pax",
    render: (value) => value.adults,
  },
  {
    title: "Childs",
    dataIndex: "pax",
    render: (value) => value.childCount,
  },
  {
    title: "Amt(Rs.)",
    dataIndex: "totalPrice",
  },
  {
    title: "Pax Email",
    dataIndex: "guestEmaiId",
  },
  {
    title: "Pax Mobile ",
    dataIndex: "guestMobileNo",
  },
  {
    title: "Star Rtng",
    dataIndex: "StarRating",
  },
];
